import React, { useState, useEffect } from 'react';
import { 
  UsersIcon, 
  ShieldCheckIcon, 
  ChartBarIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  TrashIcon,
  UserPlusIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';

interface DashboardStats {
  users: {
    total: number;
    students: number;
    teachers: number;
    admins: number;
  };
  verification: {
    verified: number;
    pending: number;
    manuallyVerified: number;
  };
  growth: {
    newUsersThisWeek: number;
    newUsersThisMonth: number;
  };
}

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  createdAt: string;
  verification?: {
    status: string;
    verifiedAt?: string;
  };
  manuallyVerified: boolean;
  manuallyVerifiedBy?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  manuallyVerifiedAt?: string;
  manualVerificationReason?: string;
  adminNotes?: string;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  
  // Filters and pagination
  const [filters, setFilters] = useState({
    role: 'all',
    verificationStatus: 'all',
    search: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Load dashboard stats
  useEffect(() => {
    loadDashboardStats();
  }, []);

  // Load users when filters change
  useEffect(() => {
    loadUsers();
  }, [filters, currentPage]);

  const loadDashboardStats = async () => {
    try {
      console.log('📊 Loading admin dashboard stats...');
      const token = localStorage.getItem('token');
      
      const response = await fetch('http://localhost:5000/api/admin/dashboard', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setStats(result.stats);
        console.log('✅ Dashboard stats loaded:', result.stats);
      } else {
        console.error('❌ Failed to load dashboard stats:', result.error);
      }
    } catch (error) {
      console.error('❌ Error loading dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      console.log('👥 Loading users with filters:', filters);
      
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...filters
      });
      
      const response = await fetch(`http://localhost:5000/api/admin/users?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setUsers(result.users);
        setTotalPages(result.pagination.total);
        console.log('✅ Users loaded:', result.users.length);
      } else {
        console.error('❌ Failed to load users:', result.error);
      }
    } catch (error) {
      console.error('❌ Error loading users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleVerifyUser = async (userId: string, reason: string = 'Manually verified by admin') => {
    try {
      console.log('✅ Verifying user:', userId);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`http://localhost:5000/api/admin/users/${userId}/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason })
      });
      
      const result = await response.json();
      
      if (result.success) {
        console.log('✅ User verified successfully');
        loadUsers(); // Refresh the list
        loadDashboardStats(); // Refresh stats
      } else {
        console.error('❌ Failed to verify user:', result.error);
        alert('Failed to verify user: ' + result.error);
      }
    } catch (error) {
      console.error('❌ Error verifying user:', error);
      alert('Error verifying user');
    }
  };

  const handleUnverifyUser = async (userId: string) => {
    try {
      console.log('❌ Removing verification for user:', userId);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`http://localhost:5000/api/admin/users/${userId}/verify`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        console.log('✅ User verification removed successfully');
        loadUsers(); // Refresh the list
        loadDashboardStats(); // Refresh stats
      } else {
        console.error('❌ Failed to remove verification:', result.error);
        alert('Failed to remove verification: ' + result.error);
      }
    } catch (error) {
      console.error('❌ Error removing verification:', error);
      alert('Error removing verification');
    }
  };

  const getVerificationBadge = (user: User) => {
    if (user.manuallyVerified || user.verification?.status === 'verified') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <CheckCircleIcon className="w-3 h-3 mr-1" />
          Verified
        </span>
      );
    } else if (user.verification?.status === 'pending') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
          Pending
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <XCircleIcon className="w-3 h-3 mr-1" />
          Unverified
        </span>
      );
    }
  };

  const getRoleBadge = (role: string) => {
    const colors = {
      student: 'bg-blue-100 text-blue-800',
      teacher: 'bg-purple-100 text-purple-800',
      admin: 'bg-red-100 text-red-800',
      super_admin: 'bg-gray-900 text-white'
    };
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors[role as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`}>
        {role.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading admin dashboard..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-1">Manage users, verifications, and platform operations</p>
            </div>
            <div className="flex items-center space-x-4">
              <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                <UserPlusIcon className="w-4 h-4" />
                <span>Add User</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UsersIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Users</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.users.total}</p>
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <span className="text-blue-600 font-medium">{stats.users.students}</span> students, 
                <span className="text-purple-600 font-medium ml-1">{stats.users.teachers}</span> teachers
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ShieldCheckIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Verified Teachers</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.verification.verified}</p>
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <span className="text-green-600 font-medium">{stats.verification.manuallyVerified}</span> manually verified
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Pending Verification</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.verification.pending}</p>
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                Awaiting review
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-8 w-8 text-indigo-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">New This Week</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.growth.newUsersThisWeek}</p>
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <span className="text-indigo-600 font-medium">{stats.growth.newUsersThisMonth}</span> this month
              </div>
            </div>
          </div>
        )}

        {/* Users Management */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">User Management</h2>
          </div>

          {/* Filters */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <select
                value={filters.role}
                onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Roles</option>
                <option value="student">Students</option>
                <option value="teacher">Teachers</option>
                <option value="admin">Admins</option>
              </select>

              <select
                value={filters.verificationStatus}
                onChange={(e) => setFilters({ ...filters, verificationStatus: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Verification</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
                <option value="manually_verified">Manually Verified</option>
              </select>
            </div>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto">
            {usersLoading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner text="Loading users..." />
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Verification
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getRoleBadge(user.role)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getVerificationBadge(user)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        {user.role === 'teacher' && !user.manuallyVerified && (
                          <button
                            onClick={() => {
                              const reason = window.prompt('Verification reason (optional):') || 'Manually verified by admin';
                              handleVerifyUser(user._id, reason);
                            }}
                            className="text-green-600 hover:text-green-900"
                          >
                            <CheckCircleIcon className="w-5 h-5" />
                          </button>
                        )}
                        
                        {user.manuallyVerified && (
                          <button
                            onClick={() => {
                              if (window.confirm('Remove manual verification?')) {
                                handleUnverifyUser(user._id);
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            <XCircleIcon className="w-5 h-5" />
                          </button>
                        )}
                        
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowUserModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 